import moment from 'moment'
import React, { useCallback, useState } from 'react'
import { useHistory, useParams } from 'react-router-dom'
import { message, Row, Col, Alert } from 'antd'

import { PageHeader } from '../Common'
import { toPath } from '../../routes'

import { usePractice } from '../../redux/slices/practices'
import { useFeatureGuard } from '../../redux/slices/session'

import { getPdfUrlFromPkg, fileToDataUrl } from '../../lib/file'
import PDFPreview from '../Common/PDFPreview'
import useSessionStorage from '../../hooks/useSessionStorage'
import OfficeCheckForm from './Form'
import CreateOfficeCheck from './UseCase/CreateOfficeCheck'
import ReportUpload from './ReportUpload'

export default function NewOfficeCheck() {
  const history = useHistory()

  const [patient, setPatient] = useState()
  const [pdfUrl, setPdfUrl] = useState()
  const [sessionFileKey, setSessionFileKey] = useState('')
  const { practiceId } = useParams()
  const practice = usePractice(practiceId)
  const { setValue } = useSessionStorage()

  const onSubmit = useCallback(
    async (payload, { setErrors }) => {
      const {
        patientId,
        firstName,
        lastName,
        dateOfBirth,
        practiceMrn,
        deviceModelId,
        deviceSerialNumber,
        dateOfCheck,
        remoteMonitoring,
      } = payload
      const showErrors = errors =>
        typeof errors === 'string' ? message.error(errors) : setErrors(errors)

      const isToday = dateOfCheck && dateOfCheck.isSame(moment(), 'day')

      try {
        const [
          patientCreationResult,
          preAuthorizationCreationResult,
        ] = await CreateOfficeCheck(patient?.id || patientId, practiceId, {
          firstName,
          lastName,
          dateOfBirth,
          practiceMrn,
          deviceModelId,
          deviceSerialNumber,
          dateOfCheck,
          remoteMonitoring,
        })
        if (
          patientCreationResult.isSuccess() &&
          preAuthorizationCreationResult.isSuccess()
        ) {
          setPatient(undefined)

          message.success('Office check successfully created')

          history.replace(
            `${
              isToday
                ? toPath.practices.officeChecks.officeCheck.edit({
                    practiceId,
                    id: preAuthorizationCreationResult.object.id,
                  })
                : toPath.practices.officeChecks({ practiceId })
            }?files=${sessionFileKey}`,
          )

          return
        }

        if (!patientCreationResult.isSuccess()) {
          showErrors(patientCreationResult.getErrors())

          return
        }

        if (!preAuthorizationCreationResult.isSuccess()) {
          setPatient(patientCreationResult.object)
          showErrors(preAuthorizationCreationResult.getErrors())
        }
      } catch (e) {
        message.error(`Something went wrong. ${e.message}`)
        throw e
      }
    },
    [history, patient, practiceId, sessionFileKey],
  )

  useFeatureGuard('in_office_checks')

  const onReportUpload = async payload => {
    // We expect:
    // - a single PKG file,
    // - a single PDF file or
    // - a PDF and a LOG file
    const pkg = payload.find(file => file.name.toLowerCase().endsWith('.pkg'))
    const pdf = payload.find(file => file.name.toLowerCase().endsWith('.pdf'))

    if (!pdf && !pkg) {
      return
    }

    const payloadKey = `iocfile_${Date.now()}_${practiceId}`

    let previewUrl = null
    if (pkg) previewUrl = await getPdfUrlFromPkg(pkg)
    else if (pdf) previewUrl = await fileToDataUrl(pdf)

    setPdfUrl(previewUrl)
    const files = payload.filter(file =>
      ['pdf', 'pkg', 'log'].includes(
        file.name
          .toLowerCase()
          .split('.')
          .pop(),
      ),
    )
    const sessionPayload = await Promise.all(
      files.map(async file => {
        const entry = {
          name: file.name,
          type: file.type || 'text/plain',
          data: await fileToDataUrl(file),
        }
        if (entry.type === 'text/plain') {
          entry.data = entry.data.replace(
            'data:application/octet-stream;',
            'data:text/plain;',
          )
        }
        return entry
      }),
    )
    setValue(payloadKey, sessionPayload)
    setSessionFileKey(payloadKey)
  }

  return (
    <div>
      <PageHeader title="Add Office Check" subTitle={practice?.name} />
      <Row gutter={{ sm: 12 }}>
        <Col span={12}>
          <Alert
            message="Please verify populated fields for accuracy, as discrete data from the device may not match the PDF exactly."
            banner
            closable
            style={{ marginBottom: '1em' }}
          />
          {!pdfUrl && <ReportUpload onUpload={onReportUpload} />}
          {pdfUrl && <PDFPreview url={pdfUrl} perPage={1} hidePagination />}
        </Col>
        <Col span={12}>
          <OfficeCheckForm practiceId={practiceId} onSubmit={onSubmit} />
        </Col>
      </Row>
    </div>
  )
}
